/*
 * Notify me (back in stock) card for sold-out variants on the product page.
 *
 * Rendered by snippets/buy-buttons.liquid as a sibling of <buy-buttons>, outside the element Prestige swaps on every
 * variant change, so the typed number and the submitted state survive a shade switch. Because it is not re-rendered,
 * it tracks the selected variant itself: the theme's <variant-picker> dispatches `variant:change` on the product form
 * with the new variant JSON, and this element shows or hides itself from `variant.available` and keeps the variant id
 * it will report up to date.
 *
 * Submits the same payload the store's original WhatsApp widget sent (phone with the 91 prefix, variant_id,
 * product_id, product_title, variant_url) with the `x-shopify-shop-domain` header that API expects.
 *
 * State lives in this module, not in storage: a success shown for a variant stays shown while the shopper toggles
 * shades on this page view, and every fresh page load asks again.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('notify-me')) {
    return;
  }

  var COUNTRY_CODE = '91';
  var PHONE_RE = /^[6-9]\d{9}$/;
  var subscribedVariants = Object.create(null);

  var digitsOnly = function (value) {
    return (value || '').replace(/\D/g, '').slice(0, 10);
  };

  var NotifyMe = class extends HTMLElement {
    connectedCallback() {
      if (this._initialised) {
        return;
      }
      this._initialised = true;

      this.apiUrl = this.dataset.apiUrl || '';
      this.input = this.querySelector('[data-nm-input]');
      this.submitButton = this.querySelector('[data-nm-submit]');
      this.buttonLabel = this.querySelector('.notify-me__button-label');
      this.spinner = this.querySelector('.notify-me__button-spinner');
      this.errorEl = this.querySelector('[data-nm-error]');
      this.formView = this.querySelector('[data-nm-view="form"]');
      this.successView = this.querySelector('[data-nm-view="success"]');

      this.abortController = new AbortController();
      var signal = this.abortController.signal;

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.submit.bind(this), { signal: signal });
      }

      if (this.input) {
        this.input.addEventListener('input', function () {
          this.input.value = digitsOnly(this.input.value);
          this.clearError();
        }.bind(this), { signal: signal });

        // The card sits inside the product form, so Enter would otherwise submit an add to cart for a sold-out variant.
        this.input.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.submit();
          }
        }.bind(this), { signal: signal });
      }

      var form = document.forms[this.getAttribute('form')];
      if (form) {
        form.addEventListener('variant:change', this.onVariantChange.bind(this), { signal: signal });
      }

      this.render();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    get variantId() {
      return this.dataset.variantId || '';
    }

    onVariantChange(event) {
      var variant = event.detail && event.detail.variant;

      if (!variant || !variant.id) {
        this.hidden = true;
        return;
      }

      this.dataset.variantId = String(variant.id);
      this.hidden = variant.available !== false;
      this.clearError();
      this.setLoading(false);
      this.render();
    }

    render() {
      var done = !!subscribedVariants[this.variantId];

      if (this.formView) {
        this.formView.hidden = done;
      }
      if (this.successView) {
        this.successView.hidden = !done;
      }
    }

    setLoading(isLoading) {
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
      }
      if (this.spinner) {
        this.spinner.hidden = !isLoading;
      }
      if (this.buttonLabel) {
        this.buttonLabel.style.visibility = isLoading ? 'hidden' : '';
      }
    }

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
      this.classList.add('is-invalid');
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      this.classList.remove('is-invalid');
    }

    submit() {
      var phone = digitsOnly(this.input ? this.input.value : '');

      if (!PHONE_RE.test(phone)) {
        this.showError('Please enter a valid 10-digit mobile number.');
        if (this.input) {
          this.input.focus();
        }
        return;
      }

      if (!this.apiUrl) {
        this.showError('Notify me is not available right now. Please try again later.');
        return;
      }

      var variantId = this.variantId;
      var payload = {
        phone: COUNTRY_CODE + phone,
        variant_id: variantId,
        product_id: this.dataset.productId || '',
        product_title: this.dataset.productTitle || '',
        variant_url: window.location.href
      };

      this.clearError();
      this.setLoading(true);

      fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-shopify-shop-domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Request failed with status ' + response.status);
          }
          subscribedVariants[variantId] = true;
          this.setLoading(false);
          this.render();
        }.bind(this))
        .catch(function () {
          this.setLoading(false);
          this.showError('Could not save your number right now. Please try again.');
        }.bind(this));
    }
  };

  window.customElements.define('notify-me', NotifyMe);
})();
